import CustomModal from "@components/common/CustomModal";
import CancelDelivery from "@forms/cancelDelivery";
import useDeliveriesCount from "@hooks/useDeliveriesCount";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import type { TCancelDelivery } from "@schemas/DeliveryForm/CancelDeliverySchema";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import { UserRank } from "@types_def/models/user.type";
import { getAdminChangeDriverDealPath } from "@utils/constants/routes";
import { canClientReactivateCancelledDelivery } from "@utils/delivery/canClientReactivateCancelledDelivery";
import { getDeliveriesCountKeyByStatus } from "@utils/getDeliveriesCountKeyByStatus";
import { enumToStr } from "@utils/strEnum";
import type React from "react";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useCancelDelivery,
  useConfirmDelivery,
  useDeleteDelivery,
  useSendRatingEmail,
  useUpdateDeliveryStatus,
  useUpdateDeliveryType,
} from "@/features/delivery/hooks/use-delivery-actions";

type Props = {
  id: number;
  status: DeliveryStatus;
  type: keyof typeof DeliveryType;
  isAdmin?: boolean;
  rank?: UserRank;
  iconOnly?: boolean;
  /** Required for correct client “reactivate” gating on cancelled rows (see {@link canClientReactivateCancelledDelivery}). */
  cancelledAt?: Date | string | null;
  smsSentAt?: Date | string | null;
};

type ButtonDetails = {
  title?: string;
  description?: string;
  action?: () => void;
  variant?: "text" | "outlined" | "contained";
  color?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  confirmText?: string;
  canOpen?: boolean;
  requireAdmin?: boolean;
  minRank?: UserRank;
  icon?: React.ReactNode;
  children?: React.ReactNode;
};

function getFilteredButtons(buttons: ButtonDetails[], isAdmin: boolean, rank?: UserRank) {
  const filteredButtons = buttons.filter((button) => {
    // Case 1: User is admin with a rank
    if (rank !== undefined && isAdmin) {
      // Only show buttons where user's rank meets minimum required rank
      const buttonMinRank = button?.minRank ?? UserRank.SUPPORT;
      return buttonMinRank >= rank;
    }
    // Case 2: User is admin without rank
    else if (isAdmin) {
      // Show all buttons to admin
      return true;
    }
    // Case 3: Regular user
    else {
      // Only show non-admin buttons
      return !button.requireAdmin;
    }
  });
  return filteredButtons;
}

function ViewDeliveryButtons({
  id,
  status,
  type,
  isAdmin = false,
  rank = undefined,
  iconOnly = false,
  cancelledAt,
  smsSentAt,
}: Props) {
  const { updateDeliveriesCount } = useDeliveriesCount();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigation = useNavigate();
  const strType = enumToStr<DeliveryType>(type);
  const strStatus = enumToStr<DeliveryStatus>(status);
  const { mutate: confirmDelivery } = useConfirmDelivery();
  const { mutate: deleteDelivery } = useDeleteDelivery();
  const { mutate: updateDeliverStatus } = useUpdateDeliveryStatus();
  const { mutate: updateDeliverType } = useUpdateDeliveryType();
  const { mutate: cancelDelivery } = useCancelDelivery();
  const { mutate: sendRatingEmail, isPending: isSendingRatingEmail = false } = useSendRatingEmail();
  const sendRatingEmailConfirmText = t("delivery.action.send-rating-email.text");

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onRemoveRestitution = useCallback(
    (id: number) => {
      void updateDeliverType(
        { UpdateDeliveryTypeInput: { id, type: DeliveryType.STANDARD } },
        {
          onSuccess: () => {
            void navigation(-1);
          },
        }
      );
    },
    [navigation, updateDeliverType]
  );

  const onTerminate = useCallback(
    (id: number) => {
      void updateDeliverStatus(
        { UpdateDeliveryStatusInput: { id, status: DeliveryStatus.COMPLETED } },
        {
          onSuccess: () => {
            updateDeliveriesCount("deliveredCount", "+");
            updateDeliveriesCount("onGoingCount", "-");
            void navigation(`/admin/pilot/details/${strType}/completed/${id}`);
          },
        }
      );
    },
    [navigation, strType, updateDeliverStatus, updateDeliveriesCount]
  );

  const onEnable = useCallback(
    (deliveryId: number) => {
      if (
        !isAdmin &&
        !canClientReactivateCancelledDelivery({
          status,
          cancelledAt,
          smsSentAt,
        })
      ) {
        return;
      }
      void updateDeliverStatus(
        { UpdateDeliveryStatusInput: { id: deliveryId, status: DeliveryStatus.REQUESTED } },
        {
          onSuccess: () => {
            updateDeliveriesCount("cancelledCount", "-");
            updateDeliveriesCount("requestedCount", "+");
            void navigation(-1);
          },
        }
      );
    },
    [
      cancelledAt,
      isAdmin,
      navigation,
      smsSentAt,
      status,
      updateDeliverStatus,
      updateDeliveriesCount,
    ]
  );

  const onCancel = useCallback(
    (data: { id: number; reason: TCancelDelivery }) => {
      const countKey = getDeliveriesCountKeyByStatus(status);
      void cancelDelivery(
        { CancelDeliveryInput: data },
        {
          onSuccess: () => {
            updateDeliveriesCount(countKey, "-");
            updateDeliveriesCount("cancelledCount", "+");
            void navigation(-1);
          },
        }
      );
    },
    [cancelDelivery, navigation, status, updateDeliveriesCount]
  );

  const onConfirm = useCallback(
    (id: number) => {
      void confirmDelivery(
        { ConfirmDeliveryInput: { deliveryId: id } },
        {
          onSuccess: () => {
            updateDeliveriesCount("confirmedCount", "+");
            updateDeliveriesCount("requestedCount", "-");
            void navigation(`/admin/pilot/create-deal/confirmed/${strType}/${id}`);
          },
        }
      );
    },
    [confirmDelivery, navigation, strType, updateDeliveriesCount]
  );

  const onDelete = useCallback(
    (id: number) => {
      void deleteDelivery(
        { DeleteInput: { id } },
        {
          onSuccess: () => {
            void navigation(-1);
          },
        }
      );
    },
    [deleteDelivery, navigation]
  );

  const onEdit = useCallback(
    (id: number): void => {
      if (isAdmin) {
        void navigation(`/admin/pilot/edit-deal/${strType}/${strStatus}/${id}`);
      } else {
        void navigation(`/client/edit-delivery/${strType}/${strStatus}/${id}`);
      }
    },
    [isAdmin, navigation, strStatus, strType]
  );

  const buttons: ButtonDetails[] = useMemo(() => {
    const envoyerEmailEvaluation: ButtonDetails = {
      title: `${t("delivery.action.send-rating-email.title")} ${id}`,
      description: t("delivery.action.send-rating-email.description"),
      action: () => {
        void sendRatingEmail(id);
      },
      variant: "contained",
      color: "info",
      confirmText: t("delivery.action.send-rating-email.text"),
      canOpen: true,
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <EmailIcon fontSize="small" />,
    };

    const annulerRestitution: ButtonDetails = {
      canOpen: true,
      confirmText: t("delivery.action.cancel-restitution.text"),
      variant: "contained",
      color: "error",
      title: `${t("delivery.action.cancel-restitution.title")} ${id}`,
      description: t("delivery.action.cancel-restitution.description"),
      action: () => onRemoveRestitution(id),
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <CloseIcon fontSize="small" />,
    };

    const changerDriver: ButtonDetails = {
      canOpen: true,
      confirmText: t("delivery.action.change-driver.text"),
      variant: "contained",
      color: "info",
      title: t("delivery.action.change-driver.title"),
      description: t("delivery.action.change-driver.description"),
      action: () => {
        void navigation(getAdminChangeDriverDealPath(strType, id));
      },
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <EditIcon fontSize="small" />,
    };

    const modifier: ButtonDetails = {
      confirmText: t("delivery.action.edit.text"),
      action: () => onEdit(id),
      variant: "contained",
      color: "primary",
      canOpen: false,
      requireAdmin: false,
      minRank: UserRank.MAINTAINER,
      icon: <EditIcon fontSize="small" />,
    };

    const annuler: ButtonDetails = {
      title: `${t("delivery.action.cancel.title")} ${id}`,
      description: t("delivery.action.cancel.description"),
      action: () => setOpen(true),
      variant: "contained",
      color: "warning",
      confirmText: t("delivery.action.cancel.text"),
      canOpen: false,
      requireAdmin: false,
      minRank: UserRank.MAINTAINER,
      icon: <CloseIcon fontSize="small" />,
    };

    const supprimer: ButtonDetails = {
      title: `${t("delivery.action.delete.title")} ${id}`,
      description: t("delivery.action.delete.description"),
      action: () => onDelete(id),
      variant: "contained",
      color: "error",
      confirmText: t("delivery.action.delete.text"),
      canOpen: true,
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <DeleteOutlineIcon fontSize="small" />,
    };

    const confirmer: ButtonDetails = {
      title: `${t("delivery.action.confirm.title")} ${id}`,
      description: t("delivery.action.confirm.description"),
      action: () => onConfirm(id),
      variant: "contained",
      color: "success",
      confirmText: t("delivery.action.confirm.text"),
      canOpen: true,
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <CheckCircleIcon fontSize="small" />,
    };

    const terminer: ButtonDetails = {
      title: `${t("delivery.action.terminate.title")} ${id}`,
      description: t("delivery.action.terminate.description"),
      action: () => onTerminate(id),
      variant: "contained",
      color: "success",
      confirmText: t("delivery.action.terminate.text"),
      canOpen: true,
      requireAdmin: true,
      minRank: UserRank.MAINTAINER,
      icon: <CheckCircleIcon fontSize="small" />,
    };

    const activer: ButtonDetails = {
      title: `${t("delivery.action.activate.title")} ${id}`,
      description: t("delivery.action.activate.description"),
      action: () => onEnable(id),
      variant: "contained",
      color: "success",
      confirmText: t("delivery.action.activate.text"),
      canOpen: true,
      requireAdmin: false,
      minRank: UserRank.MAINTAINER,
      icon: <ReplayIcon fontSize="small" />,
    };

    if (
      [
        DeliveryStatus.REQUESTED,
        DeliveryStatus.PENDING_CUSTOMER_CONFIRMATION,
        DeliveryStatus.SMS_SENT,
        DeliveryStatus.CONFIRMED_BY_CUSTOMER,
      ].includes(status)
    ) {
      const baseButtons = [annuler, supprimer, modifier, confirmer];

      if (type === DeliveryType.TWO_WAY_DELIVERY) {
        baseButtons.unshift(annulerRestitution);
      }

      return getFilteredButtons(baseButtons, isAdmin, rank);
    }

    if (DeliveryStatus.COMPLETED === status) {
      return [annuler, envoyerEmailEvaluation];
    }

    if (
      [
        DeliveryStatus.CONFIRMED,
        DeliveryStatus.WAITING_FOR_DRIVER,
        DeliveryStatus.ALERT,
        DeliveryStatus.SET,
      ].includes(status)
    ) {
      const baseButtons = [annuler, supprimer, modifier, terminer, changerDriver];

      if (type === DeliveryType.TWO_WAY_DELIVERY) {
        baseButtons.unshift(annulerRestitution);
      }

      return getFilteredButtons(baseButtons, isAdmin, rank);
    }

    if (
      [
        DeliveryStatus.CANCELLED_BY_ADMIN,
        DeliveryStatus.CANCELLED,
        DeliveryStatus.CANCELLED_BY_CUSTOMER,
        DeliveryStatus.CANCELLED_BY_MANDATORY,
      ].includes(status)
    ) {
      const clientMayReactivate =
        isAdmin ||
        canClientReactivateCancelledDelivery({
          status,
          cancelledAt,
          smsSentAt,
        });
      return getFilteredButtons(
        clientMayReactivate ? [supprimer, activer] : [supprimer],
        isAdmin,
        rank
      );
    }

    if (
      [
        DeliveryStatus.PICKUP_CLIENT,
        DeliveryStatus.PICKUP_DRIVER,
        DeliveryStatus.ONGOING,
        DeliveryStatus.SWAP_DELIVERED,
        DeliveryStatus.DELIVERED,
        DeliveryStatus.SWAP_IN_TRANSIT,
        DeliveryStatus.SWAP_PICKUP_CLIENT,
        DeliveryStatus.SWAP_PICKUP_DRIVER,
      ].includes(status)
    ) {
      return getFilteredButtons([annuler, terminer, modifier], isAdmin, rank);
    }

    return [];
  }, [
    id,
    isAdmin,
    navigation,
    onDelete,
    onEdit,
    onEnable,
    onConfirm,
    onRemoveRestitution,
    onTerminate,
    rank,
    sendRatingEmail,
    status,
    t,
    type,
    strType,
    cancelledAt,
    smsSentAt,
  ]);

  const renderTrigger = (button: ButtonDetails): React.ReactNode => {
    if (iconOnly) {
      const color = button.color ?? "primary";
      return (
        <Tooltip title={button.title ?? ""} placement="top">
          <IconButton
            aria-label={button.title ?? ""}
            size="small"
            color={color}
            sx={{ border: 1, borderColor: "divider", boxShadow: 1, "&:hover": { boxShadow: 2 } }}
          >
            {button.icon}
          </IconButton>
        </Tooltip>
      );
    }
    return button.confirmText;
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-describedby="alert-dialog-description">
        <DialogContent>
          <CancelDelivery handleClose={handleClose} id={id} handleCancelDelivery={onCancel} />
        </DialogContent>
      </Dialog>

      {iconOnly ? (
        <Stack direction="row" gap={0.75} sx={{ alignItems: "center" }}>
          {buttons.map((button) => (
            <CustomModal
              key={button.title ?? ""}
              canOpen={button.canOpen}
              fullWidth
              button={renderTrigger(button)}
              confirmText={button.confirmText}
              title={button.title}
              confirmAction={button.action}
              disabled={isSendingRatingEmail && button.confirmText === sendRatingEmailConfirmText}
              variant={button.variant}
              color={button.color}
            >
              <DialogContentText id="alert-dialog-description">
                {button.description}
              </DialogContentText>
            </CustomModal>
          ))}
        </Stack>
      ) : (
        buttons.map((button, index) => (
          <Grid
            key={`main-buttons-${button.title}`}
            size={{
              xs: index === buttons.length - 1 ? (Number(buttons.length % 2) === 0 ? 6 : 12) : 6,
              lg: 12 / buttons.length,
            }}
          >
            <CustomModal
              key={button.title ?? ""}
              canOpen={button.canOpen}
              fullWidth
              button={renderTrigger(button)}
              confirmText={button.confirmText}
              title={button.title}
              confirmAction={button.action}
              disabled={isSendingRatingEmail && button.confirmText === sendRatingEmailConfirmText}
              variant={button.variant}
              color={button.color}
            >
              <DialogContentText id="alert-dialog-description">
                {button.description}
              </DialogContentText>
            </CustomModal>
          </Grid>
        ))
      )}
    </>
  );
}

export default memo(ViewDeliveryButtons);
